<template>
  <v-card variant="outlined" class="account-list-card">
    <div class="list-card-header">
      <div>
        <div class="list-card-eyebrow">{{ $t('text.users') }}</div>
        <h3 class="list-card-title mb-1">Portal accounts</h3>
        <p class="list-card-copy mb-0">{{ activeAccountCount }} active account{{ activeAccountCount === 1 ? '' : 's' }}</p>
      </div>

      <div class="list-card-actions">
        <v-checkbox v-model="showInactive" label="Show inactive" class="ma-0" dense hide-details></v-checkbox>
        <NewUserDialog @refresh="getAccounts()"></NewUserDialog>
      </div>
    </div>

    <v-divider></v-divider>

    <div class="list-card-progress">
      <v-progress-linear v-if="loading" indeterminate height="2"></v-progress-linear>
    </div>

    <v-list class="list-card-body py-0">
      <UserListItem :header="true"></UserListItem>
      <UserListItem v-for="item in filteredAccounts" :key="item.Id" :item="item" :me="userInfo" @toggleActive="toggleActive(item)">
      </UserListItem>
    </v-list>

    <div v-if="!loading && filteredAccounts.length === 0" class="list-card-empty">
      <v-icon color="grey" size="26" class="mb-2">mdi-account-off-outline</v-icon>
      <div>No accounts to show</div>
    </div>

    <confirm-dialog ref="ConfirmDialog"></confirm-dialog>

    <v-snackbar v-model="snackbar" :timeout="timeout">
      Account deactivated
      <template v-slot:action="{ attrs }">
        <v-btn color="primary" text v-bind="attrs" @click="unToggleActive()">
          {{ $t("text.undo") }}
        </v-btn>
      </template>
    </v-snackbar>
  </v-card>
</template>


<script>
import http from "@/api/client";
import ConfirmDialog from "@/components/Dialogs/ConfirmDialog.vue";
import NewUserDialog from "@/components/Dialogs/NewUserDialog.vue";
import AuthStoreMixin from "@/helpers/mixins/AuthStoreMixin";
import UserListItem from "@/components/ListItems/UserListItem.vue";

export default {
  name: "AccountList",
  mixins: [AuthStoreMixin],
  components: {
    ConfirmDialog,
    UserListItem,
    NewUserDialog,
  },

  data() {
    return {
      hintExpanded: false,

      loading: false,
      accounts: [],
      showInactive: false,

      lastToggledId: null,
      snackbar: false,
      timeout: 5000,
    };
  },

  created() {
    this.getAccounts();
  },

  methods: {
    setAccountActive(id, isActive) {
      const itemIndex = this.accounts.findIndex((account) => account.Id === id);
      if (itemIndex >= 0) {
        this.accounts.splice(itemIndex, 1, {
          ...this.accounts[itemIndex],
          Active: isActive,
        });
      }
    },

    async getAccounts() {
      this.loading = true;
      try {
        const response = await http().get("api2/Customer/User/SearchCustomerUsers");
        this.accounts = response.data;
      }
      finally {
        this.loading = false;
      }
    },

    async unToggleActive() {
      await this.toggleActiveCall(this.lastToggledId);
      var target = this.accounts.find((x) => x.Id === this.lastToggledId);
      if (target) {
        this.setAccountActive(this.lastToggledId, !target.Active);
      }
      this.snackbar = false;
    },

    async toggleActiveCall(id) {
      var result = await http().patch("api2/customer/User/toggleactive/" + id);
      return result;
    },

    async toggleActive(item) {
      var result = true;
      //show confirm dialog if deactivating
      if (!item.Active) {
        result = await this.$refs.ConfirmDialog.show({
          title: "Deactivate " + item.Name,
          message: "Are you sure?",
          okButton: "Deactivate",
          cancelButton: "Cancel",
        });
      }
      //undo change if cancel
      if (!result) {
        this.setAccountActive(item.Id, !item.Active);
        return;
      }
      else {
        this.lastToggledId = item.Id;
        await this.toggleActiveCall(item.Id);
        this.snackbar = true;
      }
    },
  },

  computed: {
    filteredAccounts() {
      return this.accounts.filter((x) => (this.showInactive ? true : x.Active));
    },

    activeAccountCount() {
      return this.accounts.filter((account) => account.Active).length;
    },

    userInfo() {
      return this.authUserInfo;
    }
  },

  watch: {},
};
</script>

<style scoped>
.account-list-card {
  border-radius: 8px;
  overflow: hidden;
}

.list-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 16px;
}

.list-card-eyebrow {
  margin-bottom: 4px;
  color: var(--v-primary-base, #1976d2);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.list-card-title {
  color: rgba(0, 0, 0, 0.87);
  font-size: 1.1rem;
  line-height: 1.2;
}

.list-card-copy {
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.88rem;
}

.list-card-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.list-card-progress {
  min-height: 2px;
}

.list-card-body {
  background: #ffffff;
}

.list-card-empty {
  padding: 28px 16px 32px;
  color: rgba(0, 0, 0, 0.54);
  font-size: 0.92rem;
  text-align: center;
}

@media (max-width: 600px) {
  .list-card-header,
  .list-card-actions {
    align-items: stretch;
    flex-direction: column;
  }
}
</style>